import { useState } from "react";

import { Label, ListBox, ListBoxItem, Popover, DialogTrigger } from "react-aria-components";

import { Button } from "#components/v2/Button";
import { TileViewIcon } from "#icons";
import { cn } from "#utils";

export type V2AppName = "AssetDB" | "Console" | "IaC";

interface AppOption {
  label: string;
  href: string;
  isBeta: boolean;
}

const appKey = (appLabel: string) => `app-selector-${appLabel}`;

export interface AppSelectorProps {
  selected: V2AppName;
  appOptions: AppOption[];
  collapsed?: boolean;
}

export function AppSelector({ appOptions, selected, collapsed }: AppSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedAppKey = appKey(selected);

  const disabled = appOptions.length === 1;

  const selectedApp = appOptions.find((app) => app.label === selected);

  const BetaChip = (app: AppOption | undefined, isSelected?: boolean) => {
    return app?.isBeta ? (
      <span
        className={cn(
          "pl-.5 text-label-extra-small",
          isSelected ? "!text-interactive-primary-button-enabled-foreground" : "",
        )}
      >
        BETA
      </span>
    ) : null;
  };

  return (
    <DialogTrigger aria-label="App selector" isOpen={isOpen} onOpenChange={setIsOpen}>
      <div
        className={cn(
          "w-fit rounded-10 p-xs shadow outline-none transition",
          !disabled ? "hover:bg-overlay-medium hover:backdrop-blur-[15px]" : "",
          isOpen ? "opacity-0" : "",
        )}
      >
        <Button
          IconRight={<TileViewIcon className="h-[0.75em] w-auto" />}
          aria-label={`App selector - currently selected: ${selected}`}
          className={cn(
            "flex items-baseline justify-center gap-[0.5rem] rounded-10 bg-background-primary px-xs py-3xs text-label-medium outline-none transition",
            disabled
              ? "border-interactive-disabled-outline text-interactive-disabled-foreground"
              : "cursor-pointer border-interactive-secondary-button-enabled-outline text-interactive-primary-button-enabled-background hover:border-interactive-primary-button-enabled-background hover:bg-interactive-primary-button-enabled-background hover:text-interactive-primary-button-enabled-foreground",
            isOpen ? "opacity-0" : "",
          )}
          isDisabled={disabled}
          variant="plain"
        >
          {!collapsed ? (
            <>
              <span className="whitespace-nowrap">{selected}</span>
              {BetaChip(selectedApp, false)}
            </>
          ) : null}
        </Button>
      </div>
      <Popover
        offset={-40} // Negative offset to overlay the trigger position
        placement={collapsed ? "bottom left" : "bottom"}
      >
        <ListBox
          aria-label="App selection options"
          className="flex flex-col gap-[0.5rem] rounded-lg border border-background-secondary bg-interactive-glass-button-enabled-background p-xs shadow-md outline-none backdrop-blur-md"
          selectedKeys={[selectedAppKey]}
          selectionMode="single"
        >
          {appOptions
            .sort((a, b) => {
              // Selected item always comes first
              if (a.label === selected) return -1;
              if (b.label === selected) return 1;
              return 0;
            })
            .map((app) => {
              const itemKey = appKey(app.label);
              const isSelected = app.label === selected;

              return (
                <ListBoxItem
                  aria-label={app.label}
                  className={cn(
                    "size-full flex w-fit cursor-pointer items-baseline justify-center gap-[0.5rem] whitespace-nowrap rounded-10 border px-3 py-1 text-label-medium outline-none transition",
                    isSelected
                      ? "border-interactive-primary-button-enabled-background bg-interactive-primary-button-enabled-background text-interactive-primary-button-enabled-foreground"
                      : "border-interactive-secondary-button-enabled-outline bg-neutral-white/30 text-interactive-secondary-button-enabled-foreground hover:bg-interactive-secondary-button-hover-background hover:text-interactive-secondary-button-enabled-foreground active:bg-interactive-secondary-button-focus-background active:text-interactive-secondary-button-focus-foreground",
                  )}
                  href={app.href}
                  id={itemKey}
                  key={itemKey}
                  onAction={() => setIsOpen(false)}
                >
                  <Label className="whitespace-nowrap font-semibold">{app.label}</Label>
                  {isSelected ? <TileViewIcon className="h-[0.75em] w-auto" /> : null}
                  {BetaChip(app, isSelected)}
                </ListBoxItem>
              );
            })}
        </ListBox>
      </Popover>
    </DialogTrigger>
  );
}
